import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { playTone, stopAll, playBinaural, unlockAudio, isAudioUnlocked } from "@/lib/audioEngine";
import { Square, Volume2, Play } from "lucide-react";
import { SACRED_GEOMETRIES } from "./sacredGeometries";

const STEP_MS = 900; // stagger between node ignitions during PLAY GEOMETRY

const SOLFEGGIO_RING = [
  { hz: 174, label: "174" }, { hz: 285, label: "285" }, { hz: 396, label: "396" },
  { hz: 417, label: "417" }, { hz: 432, label: "432" }, { hz: 528, label: "528" },
  { hz: 639, label: "639" }, { hz: 741, label: "741" }, { hz: 852, label: "852" },
  { hz: 963, label: "963" },
];

export default function BrainLattice() {
  const canvasRef = useRef(null);
  const frameRef = useRef(null);
  const [geomId, setGeomId] = useState("tree-of-life");
  const [activeNodes, setActiveNodes] = useState(new Set());
  const [hoveredNode, setHoveredNode] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [playStep, setPlayStep] = useState(0);
  const activeTonesRef = useRef({});
  const playTimersRef = useRef([]);
  const mouseRef = useRef({ x: 0, y: 0, down: false });
  const starsRef = useRef([]);
  const ripplesRef = useRef([]); // {x, y, color, age, max}
  const streamOffsetRef = useRef(0);
  const sweepRef = useRef(0);
  const hoverPosRef = useRef({ x: 0.5, y: 0.5 });

  const geometry = useMemo(
    () => SACRED_GEOMETRIES.find(g => g.id === geomId) || SACRED_GEOMETRIES[0],
    [geomId]
  );
  const nodes = geometry.nodes;
  const connections = geometry.connections;

  // Stop everything when switching geometries
  useEffect(() => {
    stopAll();
    playTimersRef.current.forEach(t => clearTimeout(t));
    playTimersRef.current = [];
    setPlaying(false);
    setPlayStep(0);
    setActiveNodes(new Set());
    activeTonesRef.current = {};
  }, [geomId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      playTimersRef.current.forEach(t => clearTimeout(t));
      stopAll();
    };
  }, []);

  const stopAllNodes = useCallback(() => {
    stopAll();
    playTimersRef.current.forEach(t => clearTimeout(t));
    playTimersRef.current = [];
    setPlaying(false);
    setPlayStep(0);
    setActiveNodes(new Set());
    activeTonesRef.current = {};
  }, []);

  const igniteNode = useCallback((node) => {
    if (activeTonesRef.current[node.id]) return;
    if (node.hz >= 20) {
      activeTonesRef.current[node.id] = playTone({ freq: node.hz, gain: 0.10 });
    } else {
      activeTonesRef.current[node.id] = playBinaural({ carrierHz: 432, beatHz: node.hz, seconds: 600, gain: 0.22 });
    }
    // Spawn shockwave ripple at node position
    ripplesRef.current.push({ x: node.x, y: node.y, color: node.color, age: 0, max: 1.2 });
    setActiveNodes(prev => {
      const next = new Set(prev);
      next.add(node.id);
      return next;
    });
  }, []);

  const playGeometry = useCallback(() => {
    if (!isAudioUnlocked()) unlockAudio();
    // Reset
    stopAll();
    playTimersRef.current.forEach(t => clearTimeout(t));
    playTimersRef.current = [];
    activeTonesRef.current = {};
    setActiveNodes(new Set());

    setPlaying(true);
    setPlayStep(0);
    toast.success(`Activating ${geometry.name}…`, { duration: 2000 });

    geometry.nodes.forEach((node, idx) => {
      const tm = setTimeout(() => {
        igniteNode(node);
        setPlayStep(idx + 1);
        if (idx === geometry.nodes.length - 1) {
          // Stay lit after the last node ignites
          const tail = setTimeout(() => setPlaying(false), 1200);
          playTimersRef.current.push(tail);
        }
      }, idx * STEP_MS);
      playTimersRef.current.push(tm);
    });
  }, [geometry, igniteNode]);

  const toggleNode = useCallback((node) => {
    if (!isAudioUnlocked()) unlockAudio();
    setActiveNodes(prev => {
      const next = new Set(prev);
      if (next.has(node.id)) {
        next.delete(node.id);
        if (activeTonesRef.current[node.id]) {
          try { activeTonesRef.current[node.id].stop?.(); } catch {}
          delete activeTonesRef.current[node.id];
        }
      } else {
        next.add(node.id);
        if (node.hz >= 20) {
          activeTonesRef.current[node.id] = playTone({ freq: node.hz, gain: 0.12 });
        } else {
          activeTonesRef.current[node.id] = playBinaural({ carrierHz: 432, beatHz: node.hz, seconds: 600, gain: 0.25 });
        }
        ripplesRef.current.push({ x: node.x, y: node.y, color: node.color, age: 0, max: 1.2 });
        toast.message(`${node.label} — ${node.hz >= 20 ? node.hz + " Hz" : node.hz + " Hz binaural"}`, { duration: 1500 });
      }
      return next;
    });
  }, []);

  // Draw lattice — visually rich, audio-reactive
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      // Seed starfield once based on canvas size
      if (starsRef.current.length === 0) {
        for (let i = 0; i < 90; i++) {
          starsRef.current.push({
            x: Math.random(),
            y: Math.random(),
            r: Math.random() * 1.2 + 0.2,
            tw: Math.random() * Math.PI * 2,
            sp: 0.05 + Math.random() * 0.15,
          });
        }
      }
    };
    resize();

    let t = 0;
    const draw = () => {
      const w = canvas.width / dpr;
      const h = canvas.height / dpr;
      t += 0.016;
      streamOffsetRef.current = (streamOffsetRef.current + 1.4) % 30;

      // ═══ 1. Trail clear (deeper black for less smear) ═══
      ctx.fillStyle = "rgba(0,0,0,0.22)";
      ctx.fillRect(0, 0, w, h);

      const activeCount = activeNodes.size;
      const activeNodeList = nodes.filter(n => activeNodes.has(n.id));

      // ═══ 2. Aurora — radial gradient that intensifies with active count ═══
      const auroraAlpha = Math.min(0.18, 0.04 + activeCount * 0.018);
      const aurora = ctx.createRadialGradient(w * 0.5, h * 0.5, 0, w * 0.5, h * 0.5, Math.max(w, h) * 0.7);
      aurora.addColorStop(0, `rgba(255, 0, 0, ${auroraAlpha})`);
      aurora.addColorStop(0.35, `rgba(120, 0, 30, ${auroraAlpha * 0.5})`);
      aurora.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = aurora;
      ctx.fillRect(0, 0, w, h);

      // ═══ 3. Starfield with twinkle ═══
      for (const s of starsRef.current) {
        s.tw += s.sp * 0.04;
        const tw = (Math.sin(s.tw) * 0.5 + 0.5);
        ctx.fillStyle = `rgba(255, 60, 60, ${0.08 + tw * 0.25})`;
        ctx.beginPath();
        ctx.arc(s.x * w, s.y * h, s.r * (0.7 + tw * 0.6), 0, Math.PI * 2);
        ctx.fill();
      }

      // ═══ 4. Tech-grid backdrop (perspective wireframe + horizon) ═══
      ctx.save();
      const cx = w * 0.5, cy = h * 0.5;
      ctx.strokeStyle = "rgba(255,0,0,0.06)";
      ctx.lineWidth = 0.5;
      // Vertical grid lines (slight perspective via cosine)
      const cols = 18;
      for (let i = 0; i <= cols; i++) {
        const x = (i / cols) * w;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
      }
      // Horizontal lines
      const rows2 = 10;
      for (let i = 0; i <= rows2; i++) {
        const y = (i / rows2) * h;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        ctx.stroke();
      }
      // Bright horizon line
      ctx.strokeStyle = "rgba(255,0,0,0.18)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, cy);
      ctx.lineTo(w, cy);
      ctx.stroke();
      // Central vertical axis
      ctx.beginPath();
      ctx.moveTo(cx, 0);
      ctx.lineTo(cx, h);
      ctx.stroke();
      ctx.restore();

      // ═══ 5. Concentric range rings (always present, audio-reactive intensity) ═══
      ctx.save();
      const maxR = Math.min(w, h) * 0.42;
      const ringIntensity = 0.10 + Math.min(0.20, activeCount * 0.025);
      for (let k = 1; k <= 5; k++) {
        const rr = (k / 5) * maxR;
        ctx.strokeStyle = `rgba(255,0,0,${ringIntensity / k * 0.9})`;
        ctx.lineWidth = k === 3 ? 1 : 0.5;
        ctx.beginPath();
        ctx.arc(cx, cy, rr, 0, Math.PI * 2);
        ctx.stroke();
      }
      // Inner range labels
      ctx.fillStyle = "rgba(255,0,0,0.30)";
      ctx.font = "7px monospace";
      ctx.textAlign = "left";
      for (let k = 1; k <= 5; k++) {
        const rr = (k / 5) * maxR;
        ctx.fillText(`R${k}`, cx + rr + 3, cy - 2);
      }
      // Cardinal markers (N/E/S/W) on outer ring
      ctx.fillStyle = "rgba(255,0,0,0.45)";
      ctx.font = "bold 8px monospace";
      ctx.textAlign = "center";
      ctx.fillText("N", cx, cy - maxR - 6);
      ctx.fillText("S", cx, cy + maxR + 12);
      ctx.textAlign = "left";
      ctx.fillText("E", cx + maxR + 4, cy + 3);
      ctx.textAlign = "right";
      ctx.fillText("W", cx - maxR - 4, cy + 3);
      ctx.restore();

      // ═══ 6. Rotating scanner sweep beam ═══
      sweepRef.current = (sweepRef.current + 0.012) % (Math.PI * 2);
      const sweep = sweepRef.current;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(sweep);
      const sweepGrad = ctx.createLinearGradient(0, 0, maxR, 0);
      sweepGrad.addColorStop(0, "rgba(255,30,30,0.50)");
      sweepGrad.addColorStop(0.4, "rgba(255,0,0,0.15)");
      sweepGrad.addColorStop(1, "rgba(255,0,0,0)");
      ctx.strokeStyle = sweepGrad;
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(maxR, 0);
      ctx.stroke();
      // Trailing wedge
      ctx.fillStyle = "rgba(255,0,0,0.04)";
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.arc(0, 0, maxR, -0.35, 0, false);
      ctx.closePath();
      ctx.fill();
      ctx.restore();

      // ═══ 7. Central core HUD reticle (compass rose) ═══
      ctx.save();
      ctx.strokeStyle = "rgba(255,0,0,0.65)";
      ctx.lineWidth = 1;
      // Crosshair
      ctx.beginPath();
      ctx.moveTo(cx - 12, cy); ctx.lineTo(cx - 4, cy);
      ctx.moveTo(cx + 4, cy);  ctx.lineTo(cx + 12, cy);
      ctx.moveTo(cx, cy - 12); ctx.lineTo(cx, cy - 4);
      ctx.moveTo(cx, cy + 4);  ctx.lineTo(cx, cy + 12);
      ctx.stroke();
      // Center dot pulsing with active count
      const ctrPulse = Math.sin(t * 3) * 0.4 + 0.6;
      ctx.fillStyle = `rgba(255,0,0,${0.45 + ctrPulse * 0.4})`;
      ctx.beginPath();
      ctx.arc(cx, cy, 1.8, 0, Math.PI * 2);
      ctx.fill();
      // Tiny corner ticks at center
      ctx.strokeStyle = "rgba(255,0,0,0.45)";
      const tickLen = 3;
      for (const [sx, sy] of [[-1,-1],[1,-1],[-1,1],[1,1]]) {
        ctx.beginPath();
        ctx.moveTo(cx + sx * 6, cy + sy * 6);
        ctx.lineTo(cx + sx * (6 + tickLen), cy + sy * 6);
        ctx.moveTo(cx + sx * 6, cy + sy * 6);
        ctx.lineTo(cx + sx * 6, cy + sy * (6 + tickLen));
        ctx.stroke();
      }
      ctx.restore();

      // ═══ 8. Hover crosshair (follows mouse, coordinate readout) ═══
      const hp = hoverPosRef.current;
      if (hp && hoveredNode === null && mouseRef.current.x !== 0) {
        const hx = hp.x * w, hy = hp.y * h;
        ctx.save();
        ctx.strokeStyle = "rgba(255,0,0,0.35)";
        ctx.setLineDash([3, 4]);
        ctx.lineWidth = 0.6;
        ctx.beginPath();
        ctx.moveTo(0, hy); ctx.lineTo(w, hy);
        ctx.moveTo(hx, 0); ctx.lineTo(hx, h);
        ctx.stroke();
        ctx.setLineDash([]);
        // Coord text
        ctx.fillStyle = "rgba(255,0,0,0.70)";
        ctx.font = "8px monospace";
        ctx.textAlign = "left";
        ctx.fillText(`X:${hp.x.toFixed(3)}  Y:${hp.y.toFixed(3)}`, hx + 6, hy - 6);
        ctx.restore();
      }

      // ═══ 5. Sacred geometry connections + flowing particles ═══
      const nodeById = Object.fromEntries(nodes.map(n => [n.id, n]));
      if (connections.length > 0) {
        ctx.save();
        for (const [aId, bId] of connections) {
          const a = nodeById[aId], b = nodeById[bId];
          if (!a || !b) continue;
          const bothActive = activeNodes.has(aId) && activeNodes.has(bId);
          const anyActive = activeNodes.has(aId) || activeNodes.has(bId);
          const pulse = Math.sin(t * 2 + (a.x + a.y) * 10) * 0.5 + 0.5;
          const ax = a.x * w, ay = a.y * h, bx = b.x * w, by = b.y * h;

          if (bothActive) {
            // Gradient line A→B in node colors
            const grad = ctx.createLinearGradient(ax, ay, bx, by);
            grad.addColorStop(0, a.color + "FF");
            grad.addColorStop(0.5, "#FFFFFFEE");
            grad.addColorStop(1, b.color + "FF");
            ctx.strokeStyle = grad;
            ctx.lineWidth = 1.4 + pulse * 0.7;
            ctx.shadowColor = "#FFFFFF";
            ctx.shadowBlur = 6;
          } else if (anyActive) {
            const onlyA = activeNodes.has(aId);
            ctx.strokeStyle = (onlyA ? a.color : b.color) + `${Math.floor(40 + pulse * 50).toString(16).padStart(2, '0')}`;
            ctx.lineWidth = 1 + pulse * 0.3;
            ctx.shadowBlur = 0;
          } else {
            ctx.strokeStyle = "rgba(255,0,0,0.07)";
            ctx.lineWidth = 0.4;
            ctx.shadowBlur = 0;
          }
          ctx.beginPath();
          ctx.moveTo(ax, ay);
          ctx.lineTo(bx, by);
          ctx.stroke();
        }
        ctx.shadowBlur = 0;
        ctx.restore();

        // Flowing particles on bothActive connections
        ctx.save();
        for (const [aId, bId] of connections) {
          const a = nodeById[aId], b = nodeById[bId];
          if (!a || !b) continue;
          if (!(activeNodes.has(aId) && activeNodes.has(bId))) continue;
          const ax = a.x * w, ay = a.y * h, bx = b.x * w, by = b.y * h;
          const dist = Math.hypot(bx - ax, by - ay);
          const step = 30;
          const off = streamOffsetRef.current;
          for (let d = off; d < dist; d += step) {
            const px = ax + (bx - ax) * (d / dist);
            const py = ay + (by - ay) * (d / dist);
            ctx.fillStyle = "#FFFFFFEE";
            ctx.shadowColor = "#FFFFFF";
            ctx.shadowBlur = 6;
            ctx.beginPath();
            ctx.arc(px, py, 1.6, 0, Math.PI * 2);
            ctx.fill();
          }
        }
        ctx.shadowBlur = 0;
        ctx.restore();
      }

      // Emergent active-to-active web when no preset connections
      if (connections.length === 0 && activeNodeList.length > 1) {
        ctx.save();
        for (let i = 0; i < activeNodeList.length; i++) {
          for (let j = i + 1; j < activeNodeList.length; j++) {
            const a = activeNodeList[i], b = activeNodeList[j];
            const pulse = Math.sin(t * 3 + i + j) * 0.5 + 0.5;
            const grad = ctx.createLinearGradient(a.x * w, a.y * h, b.x * w, b.y * h);
            grad.addColorStop(0, a.color + "AA");
            grad.addColorStop(1, b.color + "AA");
            ctx.strokeStyle = grad;
            ctx.lineWidth = 0.5 + pulse * 1.2;
            ctx.beginPath();
            ctx.moveTo(a.x * w, a.y * h);
            ctx.lineTo(b.x * w, b.y * h);
            ctx.stroke();
          }
        }
        ctx.restore();
      }

      // ═══ 6. Shockwave ripples (advanced + cleaned) ═══
      ripplesRef.current = ripplesRef.current.filter(r => r.age < r.max);
      for (const r of ripplesRef.current) {
        r.age += 0.025;
        const progress = r.age / r.max;
        const radius = progress * 90;
        const alpha = (1 - progress) * 0.55;
        ctx.strokeStyle = r.color + Math.floor(alpha * 255).toString(16).padStart(2, '0');
        ctx.lineWidth = 2 - progress * 1.8;
        ctx.beginPath();
        ctx.arc(r.x * w, r.y * h, radius, 0, Math.PI * 2);
        ctx.stroke();
      }

      // ═══ 7. Nodes with concentric expanding rings + halo + audio-reactive pulse ═══
      for (const node of nodes) {
        const nx = node.x * w;
        const ny = node.y * h;
        const isActive = activeNodes.has(node.id);
        const isHovered = hoveredNode === node.id;
        // Pulse rate tied to hz (lower hz = slower; higher hz capped)
        const pulseRate = isActive ? Math.min(8, Math.max(1.5, node.hz / 12)) : 3;
        const pulse = isActive ? Math.sin(t * pulseRate + nodes.indexOf(node)) * 0.35 + 0.65 : 0;

        if (isActive) {
          // Outer halo
          const halo = ctx.createRadialGradient(nx, ny, 0, nx, ny, 40 + pulse * 18);
          halo.addColorStop(0, node.color + "66");
          halo.addColorStop(0.5, node.color + "22");
          halo.addColorStop(1, node.color + "00");
          ctx.fillStyle = halo;
          ctx.beginPath();
          ctx.arc(nx, ny, 40 + pulse * 18, 0, Math.PI * 2);
          ctx.fill();
          // Concentric rings
          for (let k = 0; k < 3; k++) {
            const ringPhase = (t * pulseRate * 0.4 + k * 0.6) % 1.5;
            const ringR = 8 + ringPhase * 24;
            const ringA = (1 - ringPhase / 1.5) * 0.45;
            ctx.strokeStyle = node.color + Math.floor(ringA * 255).toString(16).padStart(2, '0');
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.arc(nx, ny, ringR, 0, Math.PI * 2);
            ctx.stroke();
          }
        }

        // Core dot with glow
        const r = isActive ? 6 + pulse * 2.5 : isHovered ? 5 : 3.5;
        if (isActive) {
          ctx.shadowColor = node.color;
          ctx.shadowBlur = 12 + pulse * 6;
        }
        ctx.beginPath();
        ctx.arc(nx, ny, r, 0, Math.PI * 2);
        ctx.fillStyle = isActive ? "#FFFFFF" : isHovered ? node.color + "DD" : node.color + "40";
        ctx.fill();
        if (isActive) {
          ctx.strokeStyle = node.color;
          ctx.lineWidth = 1.5;
          ctx.stroke();
        }
        ctx.shadowBlur = 0;

        if (isHovered || isActive) {
          ctx.fillStyle = isActive ? "#FFFFFFEE" : node.color + "AA";
          ctx.font = "bold 9px monospace";
          ctx.textAlign = "center";
          ctx.fillText(node.label.toUpperCase(), nx, ny - r - 6);
          if (isActive) {
            ctx.fillStyle = node.color + "AA";
            ctx.font = "8px monospace";
            ctx.fillText(node.hz >= 20 ? `${node.hz} Hz` : `${node.hz} Hz · BIN`, nx, ny + r + 11);
          }
        }
      }

      // ═══ 8. Central core orb (intensifies with active count) ═══
      if (activeCount > 0) {
        // Compute center of mass of active nodes
        let mx = 0, my = 0;
        for (const n of activeNodeList) { mx += n.x; my += n.y; }
        mx = (mx / activeNodeList.length) * w;
        my = (my / activeNodeList.length) * h;
        const corePulse = Math.sin(t * 2.5) * 0.4 + 0.6;
        const coreR = 14 + activeCount * 3 + corePulse * 6;
        const core = ctx.createRadialGradient(mx, my, 0, mx, my, coreR);
        core.addColorStop(0, `rgba(255,255,255,${0.4 + corePulse * 0.2})`);
        core.addColorStop(0.3, `rgba(255,0,0,${0.3})`);
        core.addColorStop(1, "rgba(255,0,0,0)");
        ctx.fillStyle = core;
        ctx.beginPath();
        ctx.arc(mx, my, coreR, 0, Math.PI * 2);
        ctx.fill();
      }

      // ═══ 9. Scanline overlay (subtle) ═══
      ctx.save();
      ctx.globalAlpha = 0.06;
      for (let y = 0; y < h; y += 3) {
        ctx.fillStyle = "#000";
        ctx.fillRect(0, y, w, 1);
      }
      ctx.restore();

      // ═══ 9b. BOTTOM SPECTRUM BAR — real-time frequency readout ═══
      const specH = 36;
      const specY = h - specH - 6;
      const specPad = 14;
      ctx.save();
      // Frame
      ctx.fillStyle = "rgba(0,0,0,0.55)";
      ctx.fillRect(specPad, specY, w - specPad * 2, specH);
      ctx.strokeStyle = "rgba(255,0,0,0.35)";
      ctx.lineWidth = 0.8;
      ctx.strokeRect(specPad, specY, w - specPad * 2, specH);
      // Bars — show all 12 nodes, height = gain * pulse if active, else flat baseline
      const barCount = nodes.length;
      const usableW = w - specPad * 2 - 4;
      const barW = usableW / barCount;
      for (let i = 0; i < barCount; i++) {
        const node = nodes[i];
        const isAct = activeNodes.has(node.id);
        // Animated pulse based on hz (visual proxy — not actual FFT)
        const phase = Math.sin(t * 4 + i * 0.7);
        const lvl = isAct ? 0.4 + (phase * 0.3 + 0.7) * 0.55 : 0.06 + Math.abs(Math.sin(t * 0.6 + i)) * 0.04;
        const bx = specPad + 2 + i * barW;
        const bh = lvl * (specH - 6);
        // Bar
        const barGrad = ctx.createLinearGradient(0, specY + specH - bh, 0, specY + specH);
        barGrad.addColorStop(0, isAct ? node.color : "rgba(255,0,0,0.30)");
        barGrad.addColorStop(1, isAct ? node.color + "55" : "rgba(255,0,0,0.06)");
        ctx.fillStyle = barGrad;
        ctx.fillRect(bx, specY + specH - 3 - bh, barW - 1.5, bh);
        // Cap
        if (isAct) {
          ctx.fillStyle = "#FFFFFF";
          ctx.fillRect(bx, specY + specH - 3 - bh - 1, barW - 1.5, 1);
        }
      }
      // Spectrum label
      ctx.fillStyle = "rgba(255,0,0,0.45)";
      ctx.font = "7px monospace";
      ctx.textAlign = "left";
      ctx.fillText("SPECTRUM", specPad + 2, specY - 3);
      ctx.textAlign = "right";
      ctx.fillText(`${barCount} CH`, w - specPad - 2, specY - 3);
      ctx.restore();

      // ═══ 9c. LEFT-SIDE BRAINWAVE BAND METERS ═══
      const bandList = [
        { key: "gamma", label: "γ", color: "#E74C3C" },
        { key: "beta",  label: "β", color: "#E67E22" },
        { key: "alpha", label: "α", color: "#F1C40F" },
        { key: "theta", label: "θ", color: "#9B59B6" },
        { key: "delta", label: "δ", color: "#4A90E2" },
      ];
      ctx.save();
      const meterX = 8, meterY0 = 60, meterH = 38, meterW = 4, meterGap = 6;
      // Compute per-band counts
      const bandCounts2 = {};
      for (const n of activeNodeList) {
        bandCounts2[n.band] = (bandCounts2[n.band] || 0) + 1;
      }
      bandList.forEach((b, i) => {
        const y0 = meterY0 + i * (meterH + meterGap);
        const count = bandCounts2[b.key] || 0;
        const fill = Math.min(1, count / 3);
        // Bg
        ctx.fillStyle = "rgba(0,0,0,0.5)";
        ctx.fillRect(meterX, y0, meterW, meterH);
        ctx.strokeStyle = b.color + "55";
        ctx.lineWidth = 0.5;
        ctx.strokeRect(meterX, y0, meterW, meterH);
        // Fill
        if (fill > 0) {
          ctx.fillStyle = b.color;
          ctx.fillRect(meterX, y0 + meterH - fill * meterH, meterW, fill * meterH);
        }
        // Glyph
        ctx.fillStyle = count > 0 ? b.color : b.color + "40";
        ctx.font = "bold 10px monospace";
        ctx.textAlign = "left";
        ctx.fillText(b.label, meterX + meterW + 3, y0 + meterH - 2);
      });
      ctx.restore();

      // ═══ 10. Corner HUD brackets ═══
      ctx.save();
      ctx.strokeStyle = "rgba(255,0,0,0.5)";
      ctx.lineWidth = 1.2;
      const bL = 14;
      // TL
      ctx.beginPath(); ctx.moveTo(2, bL); ctx.lineTo(2, 2); ctx.lineTo(bL, 2); ctx.stroke();
      // TR
      ctx.beginPath(); ctx.moveTo(w - bL, 2); ctx.lineTo(w - 2, 2); ctx.lineTo(w - 2, bL); ctx.stroke();
      // BL
      ctx.beginPath(); ctx.moveTo(2, h - bL); ctx.lineTo(2, h - 2); ctx.lineTo(bL, h - 2); ctx.stroke();
      // BR
      ctx.beginPath(); ctx.moveTo(w - bL, h - 2); ctx.lineTo(w - 2, h - 2); ctx.lineTo(w - 2, h - bL); ctx.stroke();
      ctx.restore();

      // HUD readout (top-right) — TELEMETRY
      ctx.save();
      ctx.font = "8px monospace";
      ctx.textAlign = "right";
      ctx.fillStyle = "rgba(255,0,0,0.55)";
      ctx.fillText("◉ TELEMETRY", w - 8, 14);
      if (activeCount > 0) {
        const bandCounts = {};
        let hzSum = 0;
        for (const n of activeNodeList) {
          bandCounts[n.band] = (bandCounts[n.band] || 0) + 1;
          hzSum += n.hz;
        }
        const dominantBand = Object.entries(bandCounts).sort((a, b) => b[1] - a[1])[0][0].toUpperCase();
        const avgHz = (hzSum / activeNodeList.length).toFixed(1);
        ctx.fillStyle = "rgba(255,0,0,0.95)";
        ctx.fillText(`BAND: ${dominantBand}`, w - 8, 26);
        ctx.fillStyle = "rgba(255,0,0,0.75)";
        ctx.fillText(`Σ ${avgHz} Hz`, w - 8, 36);
        ctx.fillText(`${activeCount} CH ACTIVE`, w - 8, 46);
      } else {
        ctx.fillStyle = "rgba(255,0,0,0.30)";
        ctx.fillText("BAND: —", w - 8, 26);
        ctx.fillText("Σ — Hz", w - 8, 36);
        ctx.fillText("STANDBY", w - 8, 46);
      }
      // Heartbeat dot
      const hbPulse = Math.sin(t * (activeCount > 0 ? 4 : 1.6)) * 0.4 + 0.6;
      ctx.fillStyle = `rgba(255,0,0,${0.4 + hbPulse * 0.6})`;
      ctx.beginPath();
      ctx.arc(w - 12, 11, 2.2 + hbPulse * 0.6, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // TOP-LEFT geometry info badge
      ctx.save();
      ctx.font = "8px monospace";
      ctx.textAlign = "left";
      ctx.fillStyle = "rgba(255,0,0,0.55)";
      ctx.fillText("◉ GEOMETRY", 22, 14);
      ctx.fillStyle = "rgba(255,0,0,0.95)";
      ctx.fillText(geometry.name, 22, 26);
      ctx.fillStyle = "rgba(255,0,0,0.50)";
      ctx.fillText(`NODES: ${nodes.length}  CONN: ${connections.length}`, 22, 36);
      ctx.fillText(`STATUS: ${playing ? "ACTIVATING " + playStep + "/" + nodes.length : activeCount > 0 ? "LIVE" : "READY"}`, 22, 46);
      ctx.restore();

      // BOTTOM-LEFT: tuning + sample rate (cosmetic)
      ctx.save();
      ctx.font = "7px monospace";
      ctx.textAlign = "left";
      ctx.fillStyle = "rgba(255,0,0,0.40)";
      ctx.fillText("TUNING  432Hz", 22, specY - 12);
      ctx.fillText("SR  44.1kHz", 22, specY - 4);
      ctx.restore();

      frameRef.current = requestAnimationFrame(draw);
    };

    draw();
    window.addEventListener("resize", resize);
    return () => { cancelAnimationFrame(frameRef.current); window.removeEventListener("resize", resize); };
  }, [nodes, connections, activeNodes, hoveredNode, geometry.name, playing, playStep]);

  // Pointer / touch handlers
  const getNodeAt = (mx, my) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const nx = (mx - rect.left) / rect.width;
    const ny = (my - rect.top) / rect.height;
    for (const node of nodes) {
      const dist = Math.sqrt((nx - node.x) ** 2 + (ny - node.y) ** 2);
      if (dist < 0.06) return node;
    }
    return null;
  };

  const handlePointerMove = (e) => {
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const nx = (clientX - rect.left) / rect.width;
    const ny = (clientY - rect.top) / rect.height;
    mouseRef.current.x = nx;
    mouseRef.current.y = ny;
    hoverPosRef.current = { x: nx, y: ny };
    const node = getNodeAt(clientX, clientY);
    setHoveredNode(node?.id || null);
  };

  const handleDown = (e) => {
    if (!isAudioUnlocked()) unlockAudio();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    mouseRef.current.down = true;
    const node = getNodeAt(clientX, clientY);
    if (node) toggleNode(node);
    handlePointerMove(e);
  };

  const handleUp = () => { mouseRef.current.down = false; };

  const hoveredObj = hoveredNode ? nodes.find(n => n.id === hoveredNode) : null;

  return (
    <Card className="sg-frame border-[#FF0000]/30 overflow-hidden relative shadow-[0_0_40px_rgba(255,0,0,0.08)]" data-testid="brain-lattice">
      <CardHeader className="pb-1">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-[#FF0000] text-sm tracking-widest font-mono">
            &gt; FREQUENCY_MAP <span className="text-[#FF0000]/40 text-[10px] ml-1">/ {geometry.name}</span>
          </CardTitle>
          <div className="flex items-center gap-2">
            {playing && (
              <span className="text-[9px] font-mono text-[#FF0000]/60" data-testid="brain-play-progress">
                {playStep}/{geometry.nodes.length}
              </span>
            )}
            {activeNodes.size > 0 && !playing && (
              <span className="text-[9px] font-mono text-[#FF0000]/50">{activeNodes.size} active</span>
            )}
            <Button
              size="sm"
              variant="outline"
              className={`h-6 px-2 text-[8px] font-mono border-[#FF0000]/40 text-[#FF0000] ${playing ? "bg-[#FF0000]/15 animate-pulse" : ""}`}
              onClick={playGeometry}
              disabled={playing}
              data-testid="brain-play-geometry"
            >
              <Play className="w-3 h-3 mr-1" />
              {playing ? "ACTIVATING…" : "PLAY GEOMETRY"}
            </Button>
            {(activeNodes.size > 0 || playing) && (
              <Button size="sm" variant="outline" className="h-6 px-2 text-[8px] font-mono border-[#FF0000]/30 text-[#FF0000]"
                onClick={stopAllNodes} data-testid="brain-stop-all">
                <Square className="w-3 h-3 mr-1" /> STOP
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      {/* Sacred geometry preset bar */}
      <div className="px-3 pb-2">
        <div className="flex items-center gap-1 overflow-x-auto pb-1 scrollbar-thin" data-testid="geometry-presets">
          {SACRED_GEOMETRIES.map(g => (
            <button
              key={g.id}
              onClick={() => setGeomId(g.id)}
              className={`shrink-0 px-2.5 py-1 text-[9px] font-mono tracking-wider border transition-all active:scale-95 ${
                geomId === g.id
                  ? "border-[#FF0000] bg-[#FF0000]/15 text-[#FF0000] shadow-[0_0_10px_rgba(255,0,0,0.2)]"
                  : "border-[#FF0000]/15 text-[#FF0000]/40 hover:border-[#FF0000]/40 hover:text-[#FF0000]/80"
              }`}
              data-testid={`geometry-${g.id}`}
              title={g.description}
            >
              {g.short}
            </button>
          ))}
        </div>
        <p className="text-[8px] font-mono text-[#FF0000]/30 mt-1 leading-relaxed" data-testid="geometry-description">
          {geometry.description}
        </p>
      </div>

      <CardContent className="p-0">
        <div className="relative">
          <canvas ref={canvasRef}
            className="w-full cursor-crosshair"
            style={{ height: 440, touchAction: "none", background: "radial-gradient(ellipse at center, #110005 0%, #000 70%)" }}
            onPointerDown={handleDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handleUp}
            onPointerLeave={handleUp}
            onTouchStart={handleDown}
            onTouchMove={handlePointerMove}
            onTouchEnd={handleUp}
            data-testid="brain-canvas"
          />
          {/* Legend */}
          <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {[
                { band: "Delta", color: "#4A90E2", range: "0.5–4" },
                { band: "Theta", color: "#9B59B6", range: "4–8" },
                { band: "Alpha", color: "#F1C40F", range: "8–13" },
                { band: "Beta", color: "#E67E22", range: "13–30" },
                { band: "Gamma", color: "#E74C3C", range: "30+" },
              ].map(b => (
                <div key={b.band} className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: b.color, opacity: 0.7 }} />
                  <span className="text-[7px] font-mono" style={{ color: b.color + "90" }}>{b.band}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Solfeggio quick-access ring */}
        <div className="px-3 py-2 border-t border-[#FF0000]/10">
          <div className="flex items-center gap-1 mb-1.5">
            <Volume2 className="w-3 h-3 text-[#FF0000]/30" />
            <span className="text-[8px] font-mono tracking-wider text-[#FF0000]/30">SOLFEGGIO</span>
          </div>
          <div className="flex flex-wrap gap-1" data-testid="solfeggio-ring">
            {SOLFEGGIO_RING.map(s => (
              <button key={s.hz}
                onClick={() => {
                  if (!isAudioUnlocked()) unlockAudio();
                  stopAll();
                  playTone({ freq: s.hz, gain: 0.18 });
                  toast.message(`${s.hz} Hz`, { duration: 1500 });
                }}
                className="px-2 py-1 text-[9px] font-mono border border-[#FF0000]/15 text-[#FF0000]/50 hover:text-[#FF0000] hover:border-[#FF0000]/40 hover:bg-[#FF0000]/5 transition-all active:scale-95"
                data-testid={`solfeggio-${s.hz}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Hover info */}
        {hoveredObj && (
          <div className="px-3 py-1.5 border-t border-[#FF0000]/10" data-testid="brain-hover-info">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: hoveredObj.color }} />
              <span className="text-[10px] font-mono text-[#FF0000]/70">{hoveredObj.label}</span>
              <span className="text-[9px] font-mono text-[#FF0000]/40">{hoveredObj.desc}</span>
            </div>
          </div>
        )}

        <p className="text-[9px] font-mono text-[#FF0000]/20 px-3 py-1.5 border-t border-[#FF0000]/5 text-center">
          Tap nodes to activate · Switch geometry above · Headphones for binaural effect
        </p>
      </CardContent>
    </Card>
  );
}
